// useCodedVendors — the vendors a board SKU's manufacturer segment can name.
//
// One fetch per screen, not per control. A scanned purchase order is thirty
// line cards and any of them may turn out to be a board, so the page asks once
// and hands the list down; a SkuBuilder mounted inside a card must never fetch
// for itself.
//
// ANY vendor with a code, not only PCBA houses. The segment is the code, and the
// backend resolves a board's assembler by code alone (parts-catalog-service's
// attributesFromSku), so a camera module from its manufacturer or a purchased
// GPS board from its maker belongs here as much as a contract assembler does.
// This used to ask for `?type=pcba`, which offered eight board houses and made a
// vendor of any other type unreachable from the segment no matter what code it
// carried.
//
// RETIRED VENDORS ARE IN THE LIST, MARKED. The list has two jobs and only one of
// them is "what may I pick". The other is judging a code already on a SKU: a
// staged document naming a shop we stopped using is a board that shop built,
// not a code nobody owns, and `vendors_code_uniq` is global, so a new supplier
// cannot take a retired vendor's code either. Callers offer `activeCoded(...)`
// and judge against the whole list.
//
// Filtered to vendors that HAVE a code, because the segment is the code: a
// vendor with none cannot appear in a SKU, and offering it would be offering a
// choice that composes nothing.

import { useEffect, useState } from 'react';

export interface CodedVendor {
  id: string;
  code: string | null;
  name: string;
  is_disabled?: boolean;
}

interface VendorsApi {
  get: (url: string, options?: object) => Promise<any>;
}

/** The ones a picker may offer. */
export function activeCoded<T extends CodedVendor>(vendors: T[]): T[] {
  return vendors.filter((v) => !v.is_disabled);
}

/** A vendor's code as a SKU segment spells it. `vendors.code` is unique
 *  case-SENSITIVELY, so a lowercase row can exist; the segment never is. */
export function codeOf(vendor: CodedVendor): string {
  return (vendor.code || '').toUpperCase();
}

export default function useCodedVendors(api: VendorsApi): CodedVendor[] {
  const [vendors, setVendors] = useState<CodedVendor[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = (await api.get('/procurement/vendors?includeDisabled=1')) as {
          vendors?: CodedVendor[];
        };
        if (cancelled) return;
        setVendors((data.vendors || []).filter((v) => v.code));
      } catch {
        // The segment keeps its current value and offers "New vendor…"; a
        // vendors outage narrows the list rather than stopping the line.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return vendors;
}
